import { RefreshCw } from "lucide-react";
import { Button, Skeleton, Text } from "@musa.tech/design-system";

interface DashboardSectionProps {
  isLoading: boolean;
  error: Error | null;
  onRetry?: () => void;
  /** Altura do skeleton de loading — cada widget tem uma altura típica diferente. */
  loadingHeight?: string;
  /**
   * Skeleton customizado, pra widgets cujo loading não é um único bloco
   * (ex: 3 cards de KPI lado a lado). Quando ausente, cai no bloco único de
   * `loadingHeight`.
   */
  loadingContent?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Envelope de loading/erro por seção do Dashboard. Cada widget (KPIs,
 * gráfico, status, tabela) tem sua própria query — se uma falhar, só ela cai
 * pra esse estado de erro, o resto da tela continua renderizando.
 */
export const DashboardSection = ({
  isLoading,
  error,
  onRetry,
  loadingHeight = "160px",
  loadingContent,
  children,
}: DashboardSectionProps) => {
  if (isLoading) {
    return (
      loadingContent ?? (
        <Skeleton className="w-full rounded-2xl" style={{ height: loadingHeight }} />
      )
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-start gap-3 rounded-2xl border border-destructive/20 bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
        <Text variant="body" size="small" className="text-neutral-600">
          Não foi possível carregar esses dados agora.
        </Text>
        {onRetry && (
          <Button onClick={onRetry} variant="outline" size="sm">
            <RefreshCw className="mr-2 h-4 w-4" />
            Tentar novamente
          </Button>
        )}
      </div>
    );
  }

  return <>{children}</>;
};
